// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { dashboardWidgetWrapper } from "@hardwoods/Overrides/Widgets/MyAccount/dashboardStyle";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AccountMessages from "@insite/content-library/Widgets/MyAccount/AccountMessages";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { FC } from "react";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    numberOfWidgets = "numberOfWidgets",
    gap = "gap",
    includeAccountMessages = "includeAccountMessages",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfWidgets]: number;
        [fields.gap]: number;
        [fields.includeAccountMessages]: boolean;
    };
}

interface DashboardWidgetsContainerStyle {
    container?: InjectableCss;
    widgetWrapper?: InjectableCss;
}

const dashboardWidgetsContainerStyle: DashboardWidgetsContainerStyle = {
    container: {
        css: css`
            width: 100%;
            ${({ gap }: any) => {
                return css`
                    & > div {
                        margin-bottom: ${gap}px;
                        &:last-child {
                            margin-bottom: 0;
                        }
                    }
                `;
            }}
        `,
    },
    widgetWrapper: {
        css: css`
            > div {
                ${dashboardWidgetWrapper.css}
                padding-top: 20px;
                &:empty {
                    display: none;
                }
            }
        `,
    },
};

const styles = dashboardWidgetsContainerStyle;

const DashboardWidgetsRows: FC<OwnProps> = ({ id, fields }) => {
    const { numberOfWidgets = 1, gap = 75, includeAccountMessages } = fields;

    const rows: JSX.Element[] = [];
    if (includeAccountMessages) {
        rows.push(
            <StyledWrapper
                {...styles.widgetWrapper}
                key="dashboard_content_AccountMessages"
                className="dashboard-widget-wrapper"
            >
                <StyledWrapper>
                    <AccountMessages />
                </StyledWrapper>
            </StyledWrapper>,
        );
    }

    for (let i = 0; i < numberOfWidgets; i++) {
        rows.push(
            <StyledWrapper
                {...styles.widgetWrapper}
                key={`dashboard_content_${i}`}
                className="dashboard-widget-wrapper"
            >
                <Zone contentId={id} zoneName={`Content${i}`} />
            </StyledWrapper>,
        );
    }

    const props = { gap };
    return (
        <StyledWrapper {...styles.container} {...props}>
            {rows}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: DashboardWidgetsRows,
    definition: {
        displayName: "Dashboard Widgets Rows",
        group: "Dashboard" as any,
        fieldDefinitions: [
            {
                name: fields.numberOfWidgets,
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                min: 1,
            },
            {
                name: fields.gap,
                editorTemplate: "IntegerField",
                defaultValue: 75,
                fieldType: "General",
            },
            {
                name: fields.includeAccountMessages,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
